import { useEffect, useMemo, useState } from "react";
import { Outlet, useOutletContext } from "react-router-dom";
import { getBlogPagesFromPosts, type HomeCategory } from "../../lib/categories";
import { getPublishedPosts } from "../../lib/posts";
import type { Post } from "../../types/post";
import { BlankFooter } from "./BlankFooter";
import { BlankHeader } from "./BlankHeader";
import { ScrollTopButton } from "./ScrollTopButton";

export type PublicData = {
  posts: Post[];
  categories: HomeCategory[];
  loading: boolean;
  error: string | null;
};

/** Shared posts feed + derived nav categories, loaded once for every public page. */
export function usePublicData(): PublicData {
  return useOutletContext<PublicData>();
}

export function PublicLayout() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadPosts() {
      try {
        setLoading(true);
        const next = await getPublishedPosts();
        if (!ignore) {
          setPosts(next);
          setError(null);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "تعذّر تحميل المقالات."
          );
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadPosts();
    return () => {
      ignore = true;
    };
  }, []);

  const categories = useMemo(() => getBlogPagesFromPosts(posts), [posts]);

  const context: PublicData = { posts, categories, loading, error };

  return (
    <div className="blank-page">
      <BlankHeader categories={categories} />
      <main className="blank-shell">
        <Outlet context={context} />
      </main>
      <BlankFooter />
      <ScrollTopButton />
    </div>
  );
}
